import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Package, IndianRupee, Weight, Calendar, ArrowRight, Truck, UserRound } from "lucide-react";

const VEHICLE_TYPES = ["Mini Truck (Tata Ace)", "Pickup (7ft)", "LCV 14ft", "17ft Truck", "19ft Truck", "22ft Truck", "32ft SXL Container", "32ft MXL Container", "Trailer", "Bus", "Car"];
const MATERIALS = ["General Goods", "Machinery", "Textiles", "Food Grains", "Perishables", "Chemicals", "Construction Material", "Furniture", "Electronics", "Automobiles"];
const COMMODITY_TYPES = ["Personal / Home Goods", "Passenger Bus", "School Bus", "Wedding Car", "Corporate Transfer", "Commercial Freight", "Fuel Tanker", "Milk Tanker", "Refrigerated"];

export default function SenderDashboard() {
  const [loads, setLoads] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [postKind, setPostKind] = useState("consignment");

  const load = async () => {
    try {
      const [{ data: l }, { data: s }] = await Promise.all([
        api.get("/loads?scope=mine"), api.get("/stats"),
      ]);
      setLoads(l.loads);
      setStats(s);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const consignments = useMemo(() => loads.filter(l => l.kind !== "driver_hire"), [loads]);
  const drivers = useMemo(() => loads.filter(l => l.kind === "driver_hire"), [loads]);
  const active = useMemo(() => loads.filter(l => ["posted", "bidding"].includes(l.status)), [loads]);
  const confirmed = useMemo(() => loads.filter(l => ["confirmed", "in_transit"].includes(l.status)), [loads]);
  const done = useMemo(() => loads.filter(l => l.status === "delivered"), [loads]);

  const openPost = (kind) => { setPostKind(kind); setOpen(true); };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-[#FF5A00]">SENDER YARD</div>
            <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="sender-dashboard">Your consignments &amp; hires.</h1>
          </div>
          <div className="flex gap-3">
            <Button data-testid="post-load-btn" onClick={() => openPost("consignment")}
              className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-12 px-5">
              <Plus className="w-5 h-5 mr-2" /> Post Load
            </Button>
            <Button data-testid="hire-driver-btn" onClick={() => openPost("driver_hire")}
              className="rounded-sm bg-[#FFD700] hover:bg-[#FFE24E] text-black font-bold h-12 px-5">
              <UserRound className="w-5 h-5 mr-2" /> Hire Driver
            </Button>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <PostLoadDialog kind={postKind} onClose={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Total posts" value={stats.total_loads ?? 0} />
          <Stat label="Active / bidding" value={stats.active ?? 0} accent />
          <Stat label="Confirmed" value={stats.confirmed ?? 0} />
          <Stat label="In-transit" value={stats.in_transit ?? 0} />
          <Stat label="Delivered" value={stats.delivered ?? 0} />
        </div>

        <Tabs defaultValue="active" className="mt-10">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="active" data-testid="tab-active" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Active ({active.length})</TabsTrigger>
            <TabsTrigger value="consignments" data-testid="tab-consignments" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Loads ({consignments.length})</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-drivers" className="rounded-sm data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">Driver Hires ({drivers.length})</TabsTrigger>
            <TabsTrigger value="confirmed" data-testid="tab-confirmed" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Confirmed ({confirmed.length})</TabsTrigger>
            <TabsTrigger value="delivered" data-testid="tab-delivered" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Delivered ({done.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-6"><LoadGrid loads={active} empty="No active posts. Create one." /></TabsContent>
          <TabsContent value="consignments" className="mt-6"><LoadGrid loads={consignments} empty="No consignments yet." /></TabsContent>
          <TabsContent value="drivers" className="mt-6"><LoadGrid loads={drivers} empty="No driver-hire requests yet." /></TabsContent>
          <TabsContent value="confirmed" className="mt-6"><LoadGrid loads={confirmed} empty="No confirmed posts yet." /></TabsContent>
          <TabsContent value="delivered" className="mt-6"><LoadGrid loads={done} empty="No delivered posts yet." /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-[#2A2A2A] bg-[#141414]"}`}>
      <div className="overline text-zinc-400">{label}</div>
      <div className={`font-display font-black text-3xl mt-2 ${accent ? "text-[#FF5A00]" : ""}`}>{value}</div>
    </div>
  );
}

function LoadGrid({ loads, empty }) {
  if (!loads.length) return <div className="p-12 border border-dashed border-[#2A2A2A] text-center text-zinc-500">{empty}</div>;
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {loads.map((l) => <LoadCard key={l.id} load={l} />)}
    </div>
  );
}

function LoadCard({ load }) {
  const isDriver = load.kind === "driver_hire";
  const chip = isDriver
    ? "bg-[#FFD700]/15 text-[#FFD700] border-[#FFD700]/30"
    : "bg-[#FF5A00]/10 text-[#FF5A00] border-[#FF5A00]/30";
  return (
    <Link to={`/loads/${load.id}`} data-testid={`load-card-${load.id}`}
      className="group p-5 bg-[#141414] border border-[#2A2A2A] hover:border-[#FF5A00]/60 hover:-translate-y-1 transition-transform block">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className={`inline-block text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 border rounded-sm ${chip}`}>
            {isDriver ? "Driver Hire" : "Consignment"}
          </span>
          <div className="mt-2 font-display font-bold text-lg leading-tight">{load.title}</div>
        </div>
        <StatusBadge status={load.status} />
      </div>
      <div className="mt-4 flex items-center gap-2 font-mono-ll text-sm">
        <span>{load.source_city}</span><ArrowRight className="w-4 h-4 text-[#FF5A00]" /><span>{load.destination_city}</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <Meta icon={isDriver ? UserRound : Package} label={isDriver ? (load.commodity_type || "—") : load.material} />
        <Meta icon={Truck} label={load.vehicle_type} />
        <Meta icon={Calendar} label={new Date(load.pickup_date).toLocaleDateString("en-IN")} />
        <Meta icon={IndianRupee} label={formatINR(load.budget_inr)} />
      </div>
      {!isDriver && (
        <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex items-center justify-between text-sm">
          <span className="overline text-zinc-500">Weight</span>
          <span className="text-zinc-300">{load.weight_kg} kg</span>
        </div>
      )}
      {isDriver && load.trip_duration_days ? (
        <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex items-center justify-between text-sm">
          <span className="overline text-zinc-500">Trip length</span>
          <span className="text-zinc-300">{load.trip_duration_days} day(s)</span>
        </div>
      ) : null}
    </Link>
  );
}

function Meta({ icon: I, label }) {
  return (
    <div className="flex items-center gap-2 text-zinc-400">
      <I className="w-4 h-4 text-[#FF5A00]" />
      <span className="truncate">{label}</span>
    </div>
  );
}

function PostLoadDialog({ kind, onClose }) {
  const isDriver = kind === "driver_hire";
  const [f, setF] = useState({
    title: "", source_city: "", destination_city: "", material: "", weight_kg: "",
    vehicle_type: "", pickup_date: "", budget_inr: "", notes: "",
    commodity_type: "", trip_duration_days: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        kind,
        title: f.title,
        source_city: f.source_city,
        destination_city: f.destination_city,
        vehicle_type: f.vehicle_type,
        pickup_date: f.pickup_date,
        budget_inr: Number(f.budget_inr),
        notes: f.notes,
      };
      if (!isDriver) {
        payload.material = f.material;
        payload.weight_kg = Number(f.weight_kg);
      } else {
        payload.commodity_type = f.commodity_type;
        payload.trip_duration_days = Number(f.trip_duration_days || 0);
      }
      await api.post("/loads", payload);
      toast.success(isDriver ? "Driver-hire request posted." : "Load posted. Bids incoming.");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A] max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-display font-black text-2xl">
          {isDriver ? "Hire a Driver" : "Post a new consignment"}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="post-load-form">
        <Fld label="Title" span2>
          <Input required value={f.title} onChange={set("title")} data-testid="load-title-input"
            placeholder={isDriver ? "e.g. Delhi → Manali driver for 5 days" : "e.g. 5 tons cotton bales"}
            className="h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
        </Fld>
        <Fld label="Origin (city)">
          <Input required value={f.source_city} onChange={set("source_city")} data-testid="load-source-input"
            className="h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
        </Fld>
        <Fld label="Destination (city)">
          <Input required value={f.destination_city} onChange={set("destination_city")} data-testid="load-dest-input"
            className="h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
        </Fld>

        {!isDriver && (
          <>
            <Fld label="Material">
              <Select value={f.material} onValueChange={(v) => setF((s) => ({ ...s, material: v }))}>
                <SelectTrigger data-testid="load-material-select" className="h-11 bg-[#141414] border-[#2A2A2A] focus:ring-[#FF5A00] rounded-sm"><SelectValue placeholder="Pick a material" /></SelectTrigger>
                <SelectContent>{MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Weight (kg)">
              <Input required type="number" value={f.weight_kg} onChange={set("weight_kg")}
                data-testid="load-weight-input"
                className="h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
            </Fld>
          </>
        )}
        {isDriver && (
          <>
            <Fld label="Commodity / trip type">
              <Select value={f.commodity_type} onValueChange={(v) => setF((s) => ({ ...s, commodity_type: v }))}>
                <SelectTrigger data-testid="load-commodity-select" className="h-11 bg-[#141414] border-[#2A2A2A] focus:ring-[#FFD700] rounded-sm"><SelectValue placeholder="Pick commodity type" /></SelectTrigger>
                <SelectContent>{COMMODITY_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Trip length (days)">
              <Input required type="number" min="1" value={f.trip_duration_days} onChange={set("trip_duration_days")}
                data-testid="load-trip-days-input" placeholder="e.g. 5"
                className="h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FFD700] rounded-sm" />
            </Fld>
          </>
        )}

        <Fld label={isDriver ? "Vehicle class (Truck/Bus/Car/...)" : "Vehicle type"}>
          <Select value={f.vehicle_type} onValueChange={(v) => setF((s) => ({ ...s, vehicle_type: v }))}>
            <SelectTrigger data-testid="load-vehicle-select" className="h-11 bg-[#141414] border-[#2A2A2A] focus:ring-[#FF5A00] rounded-sm"><SelectValue placeholder="Vehicle" /></SelectTrigger>
            <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label={isDriver ? "Trip start date" : "Pickup date"}>
          <Input required type="date" value={f.pickup_date} onChange={set("pickup_date")}
            data-testid="load-date-input"
            className="h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
        </Fld>
        <Fld label="Budget (₹)">
          <Input required type="number" value={f.budget_inr} onChange={set("budget_inr")}
            data-testid="load-budget-input" placeholder="e.g. 25000"
            className="h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
        </Fld>
        <Fld label="Notes (optional)" span2>
          <Textarea value={f.notes} onChange={set("notes")} data-testid="load-notes-input" rows={3}
            className="bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
        </Fld>
        <DialogFooter className="sm:col-span-2">
          <Button type="submit" disabled={busy} data-testid="submit-load-btn"
            className={`rounded-sm ${isDriver ? "bg-[#FFD700] hover:bg-[#FFE24E]" : "bg-[#FF5A00] hover:bg-[#FF7A2C]"} text-black font-bold h-11 px-6`}>
            {busy ? "Posting..." : isDriver ? "Post Driver Hire" : "Post Load"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Fld({ label, span2, children }) {
  return (
    <div className={span2 ? "sm:col-span-2" : ""}>
      <Label className="overline text-zinc-400">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
